import type * as React from 'react';

import { cn } from '../../lib/utils';

export interface TooltipProps {
  label: string;
  /** Whether the tooltip is visible. Caller-controlled. */
  show: boolean;
  /** `*-end` aligns the right edges, for a control at the right edge of a clipping container. */
  placement?: 'right' | 'top' | 'top-end' | 'bottom' | 'bottom-end';
  className?: string;
  /** Optional id used for aria-describedby wiring from the trigger element. */
  id?: string;
  /**
   * Story / dev only: when true, forces the tooltip into its visible state
   * regardless of `show`. Lets Storybook autodocs render the open variant
   * without fake timers or play functions. Do NOT use in production code.
   * (See `patterns.md` Common Gotcha: "Storybook for organisms with hover
   * popovers".)
   */
  forceHoverOpen?: boolean;
}

const placementClasses: Record<NonNullable<TooltipProps['placement']>, string> = {
  right: 'left-[58px] top-1/2 -translate-y-1/2',
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
  'top-end': 'bottom-full right-0 mb-1',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
  'bottom-end': 'top-full right-0 mt-1',
};

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  show,
  placement = 'right',
  className,
  id,
  forceHoverOpen,
}) => {
  if (!show && !forceHoverOpen) return null;

  return (
    <div
      id={id}
      role="tooltip"
      className={cn(
        'absolute bg-dawn-black rounded-[4px] whitespace-nowrap z-[100] pointer-events-none',
        'px-2 py-1 shadow-[0_2px_8px_rgba(0,0,0,0.3)]',
        placementClasses[placement],
        className
      )}
    >
      <span className="font-outfit font-normal text-[12px] leading-[1.4] text-white">{label}</span>
    </div>
  );
};

Tooltip.displayName = 'Tooltip';
